/*
  Updates the server-rendered star rating on product cards with the live count
  from Yotpo.

  Yotpo is used as a data source only — its widget renders into a visually
  hidden host, the score and review count are read from the widget's accessible
  summary, and those values are written into the theme's own star markup. Cards
  therefore look identical whether the number came from Yotpo or from the
  `reviews.rating` metafield rendered by Liquid.

  The Yotpo loader auto-initialises any `.yotpo-widget-instance` present at page
  load, so the widget class is only applied once a card nears the viewport, and
  only after the page has finished loading.
*/
(() => {
  const CARD_SELECTOR = '.card-rating[data-yotpo-instance]';
  const SUMMARY_SELECTOR = '.yotpo-sr-bottom-line-summary';
  const ROOT_MARGIN = '400px 0px';
  const READY_TIMEOUT = 15000;
  const RENDER_TIMEOUT = 10000;

  let intersectionObserver;
  let readyPromise;

  const widgetsReady = () => typeof window.yotpoWidgetsContainer?.initWidget === 'function';

  const whenIdle = (callback) => {
    const schedule = () => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback, { timeout: 3000 });
      } else {
        setTimeout(callback, 200);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  };

  const whenWidgetsReady = (callback) => {
    if (!readyPromise) {
      readyPromise = new Promise((resolve, reject) => {
        if (widgetsReady()) {
          resolve();
          return;
        }

        const startedAt = Date.now();
        const poll = setInterval(() => {
          if (widgetsReady()) {
            clearInterval(poll);
            resolve();
          } else if (Date.now() - startedAt > READY_TIMEOUT) {
            clearInterval(poll);
            reject();
          }
        }, 250);
      });
    }

    readyPromise.then(callback, () => {});
  };

  // "5 out 5 stars rating in total 104 reviews. Jump to reviews."
  const readSummary = (host) => {
    const label = host.querySelector(SUMMARY_SELECTOR)?.getAttribute('aria-label');
    const match = label?.match(/([\d.]+)\s+out\s+([\d.]+)\s+stars?.*?([\d,]+)\s+reviews?/i);

    if (!match) return null;

    const score = parseFloat(match[1]);
    const max = parseFloat(match[2]);
    const count = parseInt(match[3].replace(/,/g, ''), 10);

    if (!Number.isFinite(score) || !Number.isFinite(max) || !Number.isFinite(count)) return null;

    return { score, max, count };
  };

  // Mirrors the half-star rounding the Liquid template applies.
  const roundToHalfStar = (score) => {
    const decimal = score % 1;
    if (decimal > 0.7) return 1;
    if (decimal >= 0.3) return 0.5;
    return 0;
  };

  const paint = (card, { score, max, count }) => {
    const star = card.querySelector('.rating-star');
    const rating = card.querySelector('.rating');
    const scoreText = card.querySelector('.rating-text [aria-hidden="true"]');
    const countText = card.querySelector('.rating-count [aria-hidden="true"]');
    const countLabel = card.querySelector('.rating-count .visually-hidden');

    if (star) {
      star.style.setProperty('--rating', Math.floor(score));
      star.style.setProperty('--rating-max', max);
      star.style.setProperty('--rating-decimal', roundToHalfStar(score));
    }

    if (rating) rating.setAttribute('aria-label', `${score} out of ${max} stars`);
    if (scoreText) scoreText.textContent = `${score} / ${max}`;
    if (countText) countText.textContent = `(${count})`;
    if (countLabel) countLabel.textContent = countLabel.textContent.replace(/^\s*[\d,]+/, String(count));
  };

  // The widget renders asynchronously, and renders nothing at all for a product
  // with no reviews — in which case the Liquid-rendered values simply stand.
  // A global initWidgets() — which the product page fires on every variant
  // change, including inside a quick add modal — re-fetches every element still
  // carrying the widget class, even one already rendered. Once the number has
  // been read the host has served its purpose, so let it go.
  const release = (host) => {
    host.classList.remove('yotpo-widget-instance');
    host.removeAttribute('data-yotpo-instance-id');
    host.removeAttribute('data-yotpo-product-id');
    host.textContent = '';
  };

  const watchForContent = (card, host) => {
    const settle = (state) => {
      mutationObserver.disconnect();
      clearTimeout(timer);
      release(host);
      card.setAttribute('data-yotpo-state', state);
    };

    const attempt = () => {
      const summary = readSummary(host);
      if (!summary) return false;
      paint(card, summary);
      settle('loaded');
      return true;
    };

    const mutationObserver = new MutationObserver(attempt);
    const timer = setTimeout(() => settle('empty'), RENDER_TIMEOUT);

    mutationObserver.observe(host, { childList: true, subtree: true, attributes: true });

    attempt();
  };

  const activate = (card) => {
    const host = card.querySelector('.card-rating__live');
    const instanceId = card.getAttribute('data-yotpo-instance');
    const productId = card.getAttribute('data-product-id');

    if (!host || !instanceId || !productId) return;

    card.setAttribute('data-yotpo-state', 'loading');
    host.setAttribute('data-yotpo-instance-id', instanceId);
    host.setAttribute('data-yotpo-product-id', productId);
    host.classList.add('yotpo-widget-instance');

    watchForContent(card, host);

    try {
      window.yotpoWidgetsContainer.initWidget(instanceId, host);
    } catch (e) {
      release(host);
      card.setAttribute('data-yotpo-state', 'failed');
    }
  };

  const getObserver = () => {
    if (intersectionObserver) return intersectionObserver;

    intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          whenWidgetsReady(() => activate(entry.target));
        });
      },
      { rootMargin: ROOT_MARGIN }
    );

    return intersectionObserver;
  };

  const observeCards = (root) => {
    const observer = getObserver();

    (root || document).querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (card.hasAttribute('data-yotpo-state')) return;
      observer.observe(card);
    });
  };

  // Filtering, sorting and pagination replace the grid's innerHTML, which drops
  // every widget along with it. Re-scan whatever markup replaced it.
  const watchGrid = () => {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;

    new MutationObserver(() => observeCards(grid)).observe(grid, { childList: true });
  };

  whenIdle(() => {
    observeCards();
    watchGrid();
  });
})();
